import { Navigation } from "@/components/landing/marketing-sections";
import { Button } from "@/components/ui/button";
import { motion } from "motion/react";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

type FaqEntry = {
  question: string;
  answer: string;
};

const FAQ_ENTRIES: ReadonlyArray<FaqEntry> = [
  {
    question: "What is Tilee?",
    answer:
      "Tilee is a square LED display that mirrors your current Spotify album artwork in real time, so your listening has a calm, visual presence in the room.",
  },
  {
    question: "How does it connect to Spotify?",
    answer:
      "You link your Spotify account through our secure setup flow on the device. Once paired, artwork updates automatically as you change tracks.",
  },
  {
    question: "Can I use custom artwork?",
    answer:
      "Yes. You can upload your own image so Tilee works like a square photo frame, completely independently of Spotify if you prefer. If you use Spotify, that same custom image can be shown whenever playback is paused, so the display never feels empty.",
  },
  {
    question: "What do I need to power it?",
    answer:
      "Tilee is powered over USB-C. Use the cable and adapter we recommend in the box (or an equivalent USB-C supply that meets the stated wattage).",
  },
  {
    question: "Do you ship internationally?",
    answer:
      "We ship to a growing list of regions. Checkout shows available countries and delivery estimates for your address.",
  },
  {
    question: "What is your returns policy?",
    answer:
      "If you change your mind, you can return Tilee within 30 days in original condition. See the order confirmation for the full returns process.",
  },
];

const EASE_PANEL = [0.4, 0, 0.2, 1] as const;
const EASE_CONTENT = [0.33, 1, 0.68, 1] as const;

/*
 * True when the user prefers reduced motion (client only until first paint).
 * Inputs: none.
 * Outputs: boolean.
 */
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      setReduced(mq.matches);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => {
      mq.removeEventListener("change", sync);
    };
  }, []);

  return reduced;
};

type FaqDisclosureProps = {
  answer: string;
  isOpen: boolean;
  onOpenChange: (args: { open: boolean; question: string }) => void;
  prefersReducedMotion: boolean;
  question: string;
};

/*
 * Single FAQ row: accordion behaviour with Motion height (measured) + opacity on the answer.
 * Inputs: props.question, props.answer, props.isOpen, props.onOpenChange.
 * Outputs: Accessible button + animated panel.
 */
const FaqDisclosure = (props: FaqDisclosureProps) => {
  const baseId = useId();
  const headerId = `${baseId}-header`;
  const panelId = `${baseId}-panel`;
  const measureRef = useRef<HTMLDivElement>(null);
  const [heightPx, setHeightPx] = useState(0);
  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) {
      return;
    }

    const apply = () => {
      if (!props.isOpen) {
        setHeightPx(0);
        return;
      }
      setHeightPx(el.scrollHeight);
    };

    apply();

    const ro = new ResizeObserver(() => {
      if (props.isOpen) {
        setHeightPx(el.scrollHeight);
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [props.isOpen, props.answer]);

  const panelDuration = props.prefersReducedMotion ? 0 : 0.42;
  const contentDuration = props.prefersReducedMotion ? 0 : 0.34;

  return (
    <div className="border-b border-border/80 py-4 first:pt-0">
      <button
        type="button"
        id={headerId}
        className="flex w-full cursor-pointer list-none items-center justify-between gap-4 text-left font-medium text-foreground"
        aria-expanded={props.isOpen}
        aria-controls={panelId}
        onClick={() => {
          props.onOpenChange({ question: props.question, open: !props.isOpen });
        }}
      >
        <span>{props.question}</span>
        <motion.span
          aria-hidden
          className="inline-flex shrink-0 text-muted-foreground"
          initial={false}
          animate={{ rotate: props.isOpen ? 180 : 0 }}
          transition={{ duration: panelDuration, ease: EASE_PANEL }}
        >
          <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
        </motion.span>
      </button>
      <motion.div
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        aria-hidden={!props.isOpen}
        initial={false}
        animate={{
          height: heightPx,
          opacity: props.isOpen ? 1 : 0,
        }}
        transition={{
          height: { duration: panelDuration, ease: EASE_PANEL },
          opacity: { duration: contentDuration, ease: EASE_CONTENT },
        }}
        style={{ overflow: "hidden" }}
      >
        <div ref={measureRef}>
          <p className="pt-3 text-sm leading-relaxed text-muted-foreground">{props.answer}</p>
        </div>
      </motion.div>
    </div>
  );
};

export const FaqPage = () => {
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const handleFaqOpenChange = useCallback((args: { open: boolean; question: string }) => {
    setOpenQuestion(args.open ? args.question : null);
  }, []);

  return (
    <main className="relative min-h-screen bg-background text-foreground">
      <Navigation />
      <div className="mx-auto max-w-2xl px-6 pb-20 pt-[max(8.5rem,calc(env(safe-area-inset-top,0px)+6rem))]">
        <div className="mb-10">
          <h1 className="font-serif text-4xl tracking-tight sm:text-5xl">FAQs</h1>
          <p className="mt-3 text-muted-foreground">Common questions about Tilee, delivery, and setup.</p>
        </div>
        <div>
          {FAQ_ENTRIES.map((entry) => (
            <FaqDisclosure
              key={entry.question}
              answer={entry.answer}
              question={entry.question}
              isOpen={openQuestion === entry.question}
              prefersReducedMotion={prefersReducedMotion}
              onOpenChange={handleFaqOpenChange}
            />
          ))}
        </div>
        <div className="mt-14 flex justify-center pb-4">
          <Button className="bg-foreground text-background hover:bg-foreground/90" size="lg">
            Buy now for €49
          </Button>
        </div>
      </div>
    </main>
  );
};
